/* eslint-disable */
export const pick = (o, ...keys) => {
  for (const k of keys) if (o && o[k] != null) return o[k];
  return null;
};

export const toCsvRows = (records) => {
  const rows = Array.isArray(records) ? records : [records];
  if (!rows.length) return { headers: [], lines: [] };
  const headers = Array.from(
    rows.reduce((set, r) => {
      Object.keys(r || {}).forEach(k => set.add(k));
      return set;
    }, new Set())
  );
  const escape = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map(r => headers.map(h => escape(r[h])).join(','));
  return { headers, lines };
};

export const downloadFile = (filename, blob) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Export all dashboard sections to a single CSV using current selection.
// getSelectedLocation: () => { makePK, locationType, locationPK } | null
export async function exportDashboardCsv(getSelectedLocation, filePrefix = 'OEMDashboard_All') {
  const loc = typeof getSelectedLocation === 'function' ? getSelectedLocation() : null;
  if (!loc) {
    alert("Please select filters and click Get Report before exporting.");
    return;
  }
  const payload = { makePK: loc.makePK, locationType: loc.locationType, locationPK: loc.locationPK };

  try {
    // Dashboard Inventory Metrics
    const invDashResp = await fetch('/api/OEMDashboard/GetInventoryDashboardMetrics', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin', body: JSON.stringify(payload)
    });
    if (!invDashResp.ok) throw new Error(`InventoryDashboardMetrics HTTP ${invDashResp.status}`);
    const invDashJson = await invDashResp.json();
    const invDashData = Array.isArray(invDashJson) ? (invDashJson[0] || {}) : invDashJson;

    const dashboardInventoryRecord = {
      LastMonthTotalSuggestedOrderValue: pick(invDashData, 'totalSuggestedOrderValue', 'TotalSuggestedOrderValue'),
      LastMonthTotalNonSuggestedOrderValue: pick(invDashData, 'totalNonSuggestedOrderValue', 'TotalNonSuggestedOrderValue'),
      AverageReturnPiecesThisMonth: pick(invDashData, 'averageReturnPiecesThisMonth', 'AverageReturnPiecesThisMonth', 'averageSubmittedReturnLines', 'AverageSubmittedReturnLines'),
      AverageReturnValueThisMonth: pick(invDashData, 'averageReturnValueThisMonth', 'AverageReturnValueThisMonth', 'averageSubmittedReturnValue', 'AverageSubmittedReturnValue'),
      AverageSkippedOrderCount30Days: pick(invDashData, 'averageSkippedOrderCount30Days', 'AverageSkippedOrderCount30Days', 'averageNumberOfSkippedOrders', 'AverageNumberOfSkippedOrders'),
      AverageSkippedOrderValue30Days: pick(invDashData, 'averageSkippedOrderValue30Days', 'AverageSkippedOrderValue30Days', 'averageValueOfSkippedOrders', 'AverageValueOfSkippedOrders'),
      PartsOnOverrides: pick(invDashData, 'averagePartsOnOverride', 'AveragePartsOnOverride', 'partsOnOverrides', 'PartsOnOverrides'),
      AverageTotalValueOfActiveOverrides: pick(invDashData, 'averageValueOnOverride', 'AverageValueOnOverride', 'averageTotalValueOfActiveOverrides', 'AverageTotalValueOfActiveOverrides'),
      AverageIdlePercentage: pick(invDashData, 'averageIdleInventoryPct', 'AverageIdleInventoryPct', 'averageIdlePercentage', 'AverageIdlePercentage', 'averageIdlePercent', 'AverageIdlePercent'),
      AverageIdleValue: pick(invDashData, 'averageIdleInventoryValue', 'AverageIdleInventoryValue', 'averageIdleValue', 'AverageIdleValue')
    };

    // Inventory Performance Metrics
    const perfResp = await fetch('api/OEMDashboard/GetInventoryPerformanceMetrics', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin', body: JSON.stringify(payload)
    });
    if (!perfResp.ok) throw new Error(`InventoryPerformanceMetrics HTTP ${perfResp.status}`);
    const perfData = await perfResp.json();

    const inventoryPerformanceRecord = {
      NetUtilizationPercent: perfData?.netUtilization,
      SuggestedInventoryPctOfOnHandValue: perfData?.suggestedInventoryPercentageOfOnHandValue,
      PercentageInventoryForStock: perfData?.percentageInventoryForStock,
      OffTheShelfFillRatePercentage: perfData?.offTheShelfFillRatePercentage,
      PercentageOnHandValueOverTwelveMonths: perfData?.percentageOnHandValueOverTwelveMonths,
      DaysSupply: perfData?.daysSupply
    };

    // Best/Worst Performing Inventories
    const bwResp = await fetch('api/OEMDashboard/GetBestAndWorstPerformingInventories', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin', body: JSON.stringify(payload)
    });
    if (!bwResp.ok) throw new Error(`BestAndWorstPerformingInventories HTTP ${bwResp.status}`);
    const bwData = await bwResp.json();
    const bestSorted = Array.isArray(bwData?.bestPerformingInventories) ? [...bwData.bestPerformingInventories] : [];
    const worstSorted = Array.isArray(bwData?.worstPerformingInventories) ? [...bwData.worstPerformingInventories] : [];
    bestSorted.sort((a, b) => b.partsEyePoints - a.partsEyePoints);
    worstSorted.sort((a, b) => b.partsEyePoints - a.partsEyePoints);
    const bestTop5 = bestSorted.slice(0, 5);
    const worstTop5 = worstSorted.slice(0, 5);

    const bestRows = bestTop5.map(x => ({
      DealerCode: x.dealerCode,
      DealerName: x.dealerName,
      PartsEyePoints: x.partsEyePoints,
      Location: x.location
    }));
    const worstRows = worstTop5.map(x => ({
      DealerCode: x.dealerCode,
      DealerName: x.dealerName,
      PartsEyePoints: x.partsEyePoints,
      Location: x.location
    }));

    const stamp = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
    const sectionTitle = (t) => `### ${t}`;

    const invDashCsv = toCsvRows(dashboardInventoryRecord);
    const perfCsv = toCsvRows(inventoryPerformanceRecord);
    const bestCsv = toCsvRows(bestRows);
    const worstCsv = toCsvRows(worstRows);

    const csvParts = [];
    csvParts.push(sectionTitle('Dashboard Inventory Metrics'));
    csvParts.push(invDashCsv.headers.join(','));
    csvParts.push(...invDashCsv.lines);
    csvParts.push('');
    csvParts.push(sectionTitle('Inventory Performance Metrics'));
    csvParts.push(perfCsv.headers.join(','));
    csvParts.push(...perfCsv.lines);
    csvParts.push('');
    csvParts.push(sectionTitle('Top 5 Best Performing Dealers'));
    if (bestCsv.headers.length) {
      csvParts.push(bestCsv.headers.join(','));
      csvParts.push(...bestCsv.lines);
    } else {
      csvParts.push('No dealers');
    }
    csvParts.push('');
    csvParts.push(sectionTitle('Top 5 Worst Performing Dealers'));
    if (worstCsv.headers.length) {
      csvParts.push(worstCsv.headers.join(','));
      csvParts.push(...worstCsv.lines);
    } else {
      csvParts.push('No dealers');
    }

    const combinedCsv = csvParts.join('\r\n');
    const csvBlob = new Blob([combinedCsv], { type: 'text/csv;charset=utf-8;' });
    downloadFile(`${filePrefix}_${stamp}.csv`, csvBlob);
  } catch (e) {
    console.error('Export failed', e);
    alert('Failed to export dashboard data. Please try again.');
  }
}

// Print rendered image using html2canvas.
// selector: string CSS selector of the container to capture (e.g. '.oem-dashboard')
export async function printRenderedImage(selector = '.oem-dashboard') {
  try {
    const ensureHtml2Canvas = async () => {
      if (window.html2canvas) return window.html2canvas;
      try {
        const mod = await import(/* webpackIgnore: true */'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js');
        return window.html2canvas || mod.default || mod;
      } catch {
        return window.html2canvas;
      }
    };

    const html2canvas = await ensureHtml2Canvas();
    if (!html2canvas) {
      alert("html2canvas is not available. Install via npm or include the CDN script.");
      return;
    }

    const target = document.querySelector(selector);
    if (!target) {
      alert('Nothing to print. Target selector not found.');
      return;
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'visible';

    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight
    });

    document.body.style.overflow = prevOverflow;

    const dataUrl = canvas.toDataURL('image/png');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Popup blocked. Please allow popups to print.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Dashboard Print</title>
          <style>
            @page { size: A4; margin: 12mm; }
            html, body { margin: 0; padding: 0; }
            img { width: 100%; height: auto; }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          </style>
        </head>
        <body>
          <img id="print-image" src="${dataUrl}" alt="Dashboard" />
          <script>
            const img = document.getElementById('print-image');
            img.onload = function() {
              setTimeout(function(){ window.print(); window.close(); }, 200);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  } catch (err) {
    console.error('Print rendered image failed', err);
    alert('Failed to generate print image.');
  }
}